import React, { useState, useEffect } from 'react';
import { 
  Route, Plus, Pencil, Trash2, ArrowRight, Printer, MapPin,
  GripVertical, ChevronUp, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { getBranchAuthHeaders } from '@/utils/branchApi';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MATCH_TYPES = [
  { value: 'all', label: 'All Items', description: 'Match all items in ticket' },
  { value: 'category', label: 'By Category', description: 'Match items by category ID' },
  { value: 'item', label: 'Specific Item', description: 'Match specific menu item' },
  { value: 'contains', label: 'Name Contains', description: 'Match items containing text' }
];

const PRINT_GROUPS = [
  { value: 'TICKET', label: 'Ticket', description: 'Standard kitchen ticket' },
  { value: 'BAR', label: 'Bar', description: 'Bar/drink orders' },
  { value: 'EXPO', label: 'Expo', description: 'Expeditor station' }
];

export default function KitchenRoutingPage() {
  const [rules, setRules] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    station_id: '',
    printer_id: '',
    match_type: 'all',
    match_value: '',
    match_category_id: '',
    match_menu_item_id: '',
    match_contains_text: '',
    station_code: 'ALL',
    print_group: 'TICKET',
    header_label: '',
    priority: 100,
    copies: 1,
    enabled: true
  });

  const fetchData = async () => {
    try {
      const [rulesRes, printersRes, stationsRes] = await Promise.all([
        fetch(`${API_URL}/api/kitchen/routing-rules`, { headers: getBranchAuthHeaders() }),
        fetch(`${API_URL}/api/kitchen/printers`, { headers: getBranchAuthHeaders() }),
        fetch(`${API_URL}/api/kitchen/stations`, { headers: getBranchAuthHeaders() })
      ]);
      
      if (rulesRes.ok) setRules(await rulesRes.json());
      if (printersRes.ok) setPrinters(await printersRes.json());
      if (stationsRes.ok) setStations(await stationsRes.json());
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openAddModal = () => {
    setEditingRule(null);
    setFormData({
      station_id: '',
      printer_id: printers[0]?.id || '',
      match_type: 'all',
      match_value: '',
      match_category_id: '',
      match_menu_item_id: '',
      match_contains_text: '',
      station_code: 'ALL',
      print_group: 'TICKET',
      header_label: '',
      priority: rules.length > 0 ? Math.max(...rules.map(r => r.priority)) + 10 : 100,
      copies: 1,
      enabled: true
    });
    setIsModalOpen(true);
  };

  const openEditModal = (rule) => {
    setEditingRule(rule);
    setFormData({
      station_id: rule.station_id || '',
      printer_id: rule.printer_id,
      match_type: rule.match_type,
      match_value: rule.match_value || '',
      match_category_id: rule.match_category_id || '',
      match_menu_item_id: rule.match_menu_item_id || '',
      match_contains_text: rule.match_contains_text || '',
      station_code: rule.station_code || 'ALL',
      print_group: rule.print_group || 'TICKET',
      header_label: rule.header_label || '',
      priority: rule.priority,
      copies: rule.copies,
      enabled: rule.enabled
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.printer_id) {
      toast.error('Please select a printer');
      return;
    }
    
    try {
      const url = editingRule 
        ? `${API_URL}/api/kitchen/routing-rules/${editingRule.id}`
        : `${API_URL}/api/kitchen/routing-rules`;
      
      const method = editingRule ? 'PATCH' : 'POST';
      
      const payload = {
        ...formData,
        station_id: formData.station_id || null,
        match_value: formData.match_value || null,
        match_category_id: formData.match_category_id || null,
        match_menu_item_id: formData.match_menu_item_id || null,
        match_contains_text: formData.match_contains_text || null,
        header_label: formData.header_label || null
      };
      
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getBranchAuthHeaders()
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success(editingRule ? 'Rule updated' : 'Rule created');
        setIsModalOpen(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save rule');
      }
    } catch (err) {
      toast.error('Error saving rule');
    }
  };

  const deleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/kitchen/routing-rules/${ruleId}`, {
        method: 'DELETE',
        headers: getBranchAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Rule deleted');
        fetchData();
      } else {
        toast.error('Failed to delete rule');
      }
    } catch (err) {
      toast.error('Error deleting rule');
    }
  };

  const toggleEnabled = async (rule) => {
    try {
      const res = await fetch(`${API_URL}/api/kitchen/routing-rules/${rule.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getBranchAuthHeaders()
        },
        body: JSON.stringify({ enabled: !rule.enabled })
      });
      
      if (res.ok) {
        toast.success(`Rule ${!rule.enabled ? 'enabled' : 'disabled'}`);
        fetchData();
      }
    } catch (err) {
      toast.error('Error updating rule');
    }
  };

  const updatePriority = async (rule, direction) => {
    const newPriority = direction === 'up' ? rule.priority - 15 : rule.priority + 15;
    
    try {
      const res = await fetch(`${API_URL}/api/kitchen/routing-rules/${rule.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getBranchAuthHeaders()
        },
        body: JSON.stringify({ priority: Math.max(1, newPriority) })
      });
      
      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      toast.error('Error updating priority');
    }
  };

  const getMatchTypeLabel = (type) => {
    return MATCH_TYPES.find(t => t.value === type)?.label || type;
  };

  const getMatchDisplay = (rule) => {
    switch (rule.match_type) {
      case 'category':
        return rule.match_category_id || rule.match_value;
      case 'item':
        return rule.match_menu_item_id || rule.match_value;
      case 'contains':
        return `"${rule.match_contains_text || rule.match_value}"`;
      default:
        return null;
    }
  };

  // Sort rules by priority
  const sortedRules = [...rules].sort((a, b) => a.priority - b.priority);

  return (
    <div data-testid="kitchen-routing-page" className="p-6 dark:bg-[#011627]">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 dark:text-[#d6deeb]">
            <Route className="w-6 h-6" /> Routing Rules
          </h1>
          <p className="text-muted-foreground dark:text-[#7fdbca]">
            Configure which items print to which printers (lower priority = higher precedence)
          </p>
        </div>
        <Button onClick={openAddModal} data-testid="add-rule-btn" disabled={printers.length === 0}>
          <Plus className="w-4 h-4 mr-2" /> Add Rule
        </Button>
      </div>

      {printers.length === 0 && (
        <Card className="mb-6 border-yellow-500">
          <CardContent className="py-4">
            <p className="text-yellow-600">
              No printers configured. Please add a printer first before creating routing rules.
            </p>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-10">Loading...</div>
      ) : sortedRules.length === 0 ? (
        <Card>
          <CardContent className="text-center py-10">
            <Route className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium">No routing rules configured</h3>
            <p className="text-muted-foreground mb-4">
              Create rules to automatically route tickets to printers
            </p>
            {printers.length > 0 && (
              <Button onClick={openAddModal}>
                <Plus className="w-4 h-4 mr-2" /> Add Rule
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {sortedRules.map((rule, idx) => (
            <Card 
              key={rule.id} 
              data-testid={`rule-card-${rule.id}`}
              className={cn(!rule.enabled && 'opacity-60')}
            >
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* Priority Controls */}
                    <div className="flex flex-col gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={() => updatePriority(rule, 'up')}
                        disabled={idx === 0}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <span className="text-xs text-center text-muted-foreground w-6">
                        {rule.priority}
                      </span>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={() => updatePriority(rule, 'down')}
                        disabled={idx === sortedRules.length - 1}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {/* Match Type */}
                    <div className="flex items-center gap-2 min-w-[180px]">
                      <Badge variant="outline">
                        {getMatchTypeLabel(rule.match_type)}
                      </Badge>
                      {getMatchDisplay(rule) && (
                        <span className="text-sm text-muted-foreground">
                          {getMatchDisplay(rule)}
                        </span>
                      )}
                    </div>
                    
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    
                    {/* Station Code */}
                    <div className="flex items-center gap-1 min-w-[100px]">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      <Badge variant="secondary">{rule.station_code}</Badge>
                    </div>
                    
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    
                    {/* Printer */}
                    <div className="flex items-center gap-1">
                      <Printer className="w-4 h-4" />
                      <span className="font-medium">{rule.printer_name}</span>
                    </div>
                    
                    {/* Print Group & Header */}
                    <div className="flex items-center gap-2">
                      <Badge>{rule.print_group}</Badge>
                      {rule.header_label && (
                        <Badge variant="outline">{rule.header_label}</Badge>
                      )}
                    </div>
                    
                    {/* Copies */}
                    {rule.copies > 1 && (
                      <Badge variant="secondary">x{rule.copies}</Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Badge variant={rule.enabled ? 'default' : 'secondary'}>
                      {rule.enabled ? 'Active' : 'Disabled'}
                    </Badge>
                    <Button size="sm" variant="outline" onClick={() => openEditModal(rule)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => toggleEnabled(rule)}>
                      {rule.enabled ? 'Disable' : 'Enable'}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-red-500"
                      onClick={() => deleteRule(rule.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingRule ? 'Edit Routing Rule' : 'Add Routing Rule'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="matching" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="matching">Matching</TabsTrigger>
                <TabsTrigger value="output">Output</TabsTrigger>
              </TabsList>
              
              <TabsContent value="matching" className="space-y-4 py-4">
                <div>
                  <Label>Match Type *</Label>
                  <Select 
                    value={formData.match_type} 
                    onValueChange={(value) => setFormData({...formData, match_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MATCH_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div>
                            <div>{type.label}</div>
                            <div className="text-xs text-muted-foreground">{type.description}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.match_type === 'category' && (
                  <div>
                    <Label>Category ID</Label>
                    <Input
                      value={formData.match_category_id || formData.match_value}
                      onChange={(e) => setFormData({
                        ...formData, 
                        match_category_id: e.target.value,
                        match_value: e.target.value
                      })}
                      placeholder="e.g., 1 or drinks"
                    />
                  </div>
                )}
                
                {formData.match_type === 'item' && (
                  <div>
                    <Label>Menu Item ID</Label>
                    <Input
                      value={formData.match_menu_item_id || formData.match_value}
                      onChange={(e) => setFormData({
                        ...formData, 
                        match_menu_item_id: e.target.value,
                        match_value: e.target.value
                      })}
                      placeholder="e.g., 42"
                    />
                  </div>
                )}
                
                {formData.match_type === 'contains' && (
                  <div>
                    <Label>Item Name Contains</Label>
                    <Input
                      value={formData.match_contains_text || formData.match_value}
                      onChange={(e) => setFormData({
                        ...formData, 
                        match_contains_text: e.target.value,
                        match_value: e.target.value
                      })}
                      placeholder="e.g., Cocktail"
                    />
                  </div>
                )}
                
                <div>
                  <Label>Priority</Label>
                  <Input
                    type="number"
                    min={1}
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value) || 100})}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Lower number = higher priority. Rules are evaluated in priority order.
                  </p>
                </div>
              </TabsContent>
              
              <TabsContent value="output" className="space-y-4 py-4">
                <div>
                  <Label>Station Code</Label>
                  <Select 
                    value={formData.station_code} 
                    onValueChange={(value) => setFormData({...formData, station_code: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">ALL (Any station)</SelectItem>
                      {stations.map(s => (
                        <SelectItem key={s.id} value={s.code}>{s.name} ({s.code})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Printer *</Label>
                  <Select 
                    value={formData.printer_id} 
                    onValueChange={(value) => setFormData({...formData, printer_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select printer" />
                    </SelectTrigger>
                    <SelectContent>
                      {printers.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} ({p.connection})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Print Group</Label>
                  <Select 
                    value={formData.print_group} 
                    onValueChange={(value) => setFormData({...formData, print_group: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRINT_GROUPS.map(g => (
                        <SelectItem key={g.value} value={g.value}>
                          <div>
                            <div>{g.label}</div>
                            <div className="text-xs text-muted-foreground">{g.description}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Header Label (Optional)</Label>
                  <Input
                    value={formData.header_label}
                    onChange={(e) => setFormData({...formData, header_label: e.target.value})}
                    placeholder="e.g., GRILL STATION"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Custom label printed at the top of the ticket
                  </p>
                </div>
                
                <div>
                  <Label>Copies</Label>
                  <Input
                    type="number"
                    min={1}
                    max={5}
                    value={formData.copies}
                    onChange={(e) => setFormData({...formData, copies: parseInt(e.target.value) || 1})}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <Label>Enabled</Label>
                  <Switch
                    checked={formData.enabled}
                    onCheckedChange={(checked) => setFormData({...formData, enabled: checked})}
                  />
                </div>
              </TabsContent>
            </Tabs>
            
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {editingRule ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
